import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import {
  Activity, AlertTriangle, MapPin, Route as RouteIcon, Users,
  Plus, ArrowUpRight, Radio, QrCode, CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ActivityFeed } from "@/components/ActivityFeed";
import { trackCta } from "@/lib/app-analytics";
import { OperationsRoleGate, OPS_COMMAND_ROLES } from "@/components/OperationsRoleGate";
import { FieldPresenceStrip } from "@/components/dashboards/FieldPresenceStrip";
import { useFieldPresence } from "@/lib/dashboard/use-field-presence";

export const Route = createFileRoute("/_authenticated/operations/")({
  component: OperationsRoute,
  head: () => ({ meta: [{ title: "Operations Center — MissionReady" }] }),
});

function OperationsRoute() {
  return (
    <OperationsRoleGate allowed={OPS_COMMAND_ROLES}>
      <OperationsPage />
    </OperationsRoleGate>
  );
}

function OperationsPage() {
  const { currentOrg, user, isPlatformAdmin } = useAuth();
  const presence = useFieldPresence(30_000);
  const [counts, setCounts] = useState({ sites: 0, members: 0, openIncidents: 0, criticalIncidents: 0, activePatrols: 0, pendingAssignments: 0 });
  const [criticalIncidents, setCriticalIncidents] = useState<any[]>([]);
  const [recentIncidents, setRecentIncidents] = useState<any[]>([]);

  useEffect(() => {
    if (!currentOrg) return;
    const orgId = currentOrg.id;
    const load = async () => {
      const [sites, members, incidentsCount, criticalCount, patrols, assignments, critical, recent] = await Promise.all([
        supabase.from("sites").select("*", { count: "exact", head: true }).eq("organization_id", orgId),
        supabase.from("organization_members").select("*", { count: "exact", head: true }).eq("organization_id", orgId),
        supabase.from("incidents").select("*", { count: "exact", head: true }).eq("organization_id", orgId).in("status", ["open", "in_progress"]),
        supabase.from("incidents").select("*", { count: "exact", head: true }).eq("organization_id", orgId).eq("severity", "critical").not("status", "in", "(closed,resolved)"),
        supabase.from("patrols").select("*", { count: "exact", head: true }).eq("organization_id", orgId).eq("status", "active"),
        supabase.from("dispatch_assignments").select("*", { count: "exact", head: true }).eq("organization_id", orgId).in("status", ["pending", "accepted", "en_route"]),
        supabase.from("incidents").select("id, reference_number, title, subtype, category, severity, status, occurrence_at, escalated").eq("organization_id", orgId).or("severity.eq.critical,escalated.eq.true").not("status", "in", "(closed,resolved)").order("created_at", { ascending: false }).limit(6),
        supabase.from("incidents").select("id, reference_number, title, subtype, category, severity, status, created_at").eq("organization_id", orgId).order("created_at", { ascending: false }).limit(6),
      ]);
      setCounts({
        sites: sites.count ?? 0, members: members.count ?? 0,
        openIncidents: incidentsCount.count ?? 0, criticalIncidents: criticalCount.count ?? 0,
        activePatrols: patrols.count ?? 0, pendingAssignments: assignments.count ?? 0,
      });
      setCriticalIncidents((critical.data as any[]) ?? []);
      setRecentIncidents((recent.data as any[]) ?? []);
    };
    load();
    const ch = supabase.channel(`ops:${orgId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "incidents", filter: `organization_id=eq.${orgId}` }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "patrols", filter: `organization_id=eq.${orgId}` }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "dispatch_assignments", filter: `organization_id=eq.${orgId}` }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [currentOrg]);

  const trialDays = currentOrg?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(currentOrg.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  const stats = [
    { label: "Sites", value: counts.sites, icon: MapPin, to: "/sites" as const },
    { label: "Team", value: counts.members, icon: Users, to: "/team" as const },
    { label: "Open incidents", value: counts.openIncidents, icon: AlertTriangle, to: "/incidents" as const },
    { label: "Critical", value: counts.criticalIncidents, icon: AlertTriangle, to: "/incidents" as const, accent: counts.criticalIncidents > 0 },
    { label: "Active guard tours", value: counts.activePatrols, icon: RouteIcon, to: "/guard-tours" as const },
    { label: "Active dispatch", value: counts.pendingAssignments, icon: Radio, to: "/console/dispatch" as const },
  ];

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Operations Center</p>
          <h1 className="font-display text-2xl font-bold md:text-3xl">
            Welcome back{user?.email ? `, ${user.email.split("@")[0]}` : ""}.
          </h1>
          <p className="text-sm text-muted-foreground">{currentOrg?.name}</p>
        </div>
        <div className="flex gap-2">
          <Button asChild size="sm" variant="outline" onClick={() => trackCta("ops_report_incident")}><Link to="/incidents/new"><Plus className="mr-1 h-4 w-4" /> Report incident</Link></Button>
          <Button asChild size="sm" onClick={() => trackCta("ops_open_dispatch")}><Link to="/console/dispatch"><Radio className="mr-1 h-4 w-4" /> Dispatch</Link></Button>
        </div>
      </div>

      {/* Primary above-the-fold actions — keep guards and supervisors moving */}
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <Link
          to="/guard-tours"
          onClick={() => trackCta("ops_start_guard_tour")}
          className="group flex items-center gap-3 rounded-2xl border border-primary/40 bg-primary/10 p-4 transition-all hover:border-primary hover:bg-primary/15"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground"><QrCode className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-primary">Start guard tour</div>
            <div className="truncate text-sm text-muted-foreground">Scan or pick a checkpoint to begin</div>
          </div>
          <ArrowUpRight className="h-4 w-4 text-primary opacity-70 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </Link>
        <Link
          to="/schedule"
          search={{ view: "today" }}
          onClick={() => trackCta("ops_view_today_shifts")}
          className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-4 transition-all hover:border-primary/40 hover:shadow-card"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-secondary-foreground"><CalendarClock className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-foreground">Today&apos;s shifts</div>
            <div className="truncate text-sm text-muted-foreground">See who&apos;s on duty right now</div>
          </div>
          <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-70 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </Link>
        <Link
          to="/incidents"
          onClick={() => trackCta("ops_recent_incidents")}
          className={`group flex items-center gap-3 rounded-2xl border p-4 transition-all hover:shadow-card ${counts.criticalIncidents > 0 ? "border-destructive/50 bg-destructive/10 hover:border-destructive" : "border-border bg-card hover:border-primary/40"}`}
        >
          <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${counts.criticalIncidents > 0 ? "bg-destructive text-destructive-foreground" : "bg-warning text-warning-foreground"}`}><AlertTriangle className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <div className={`text-xs font-semibold uppercase tracking-wider ${counts.criticalIncidents > 0 ? "text-destructive" : "text-foreground"}`}>Recent incidents</div>
            <div className="truncate text-sm text-muted-foreground">{counts.openIncidents} open · {counts.criticalIncidents} critical</div>
          </div>
          <ArrowUpRight className="h-4 w-4 opacity-70 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </Link>
      </div>

      {currentOrg?.plan === "trial" && trialDays !== null && !isPlatformAdmin && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm">
          <div>
            <span className="font-semibold text-warning">Free trial</span>
            <span className="ml-2 text-muted-foreground">{trialDays} day{trialDays === 1 ? "" : "s"} remaining</span>
          </div>
          <Button asChild size="sm" variant="outline"><Link to="/billing">Upgrade</Link></Button>
        </div>
      )}

      <div className="mt-6">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-semibold">Live field status</h2>
          </div>
          <Link to="/operations/live" className="text-xs text-primary hover:underline">Open Live Operations</Link>
          <Link to="/operations/evidence" className="ml-3 text-xs text-primary hover:underline">AI Evidence</Link>
        </div>
        <div className="mt-3">
          <FieldPresenceStrip presence={presence} />
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        {stats.map((s) => (
          <Link key={s.label} to={s.to}
            className={`group rounded-xl border bg-card p-5 transition-all hover:shadow-card ${s.accent ? "border-destructive/50 bg-destructive/5 hover:border-destructive" : "border-border hover:border-primary/40"}`}>
            <div className="flex items-center justify-between">
              <s.icon className={`h-4 w-4 ${s.accent ? "text-destructive" : "text-primary"}`} />
              <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </div>
            <div className={`mt-3 font-display text-3xl font-semibold ${s.accent ? "text-destructive" : ""}`}>{s.value}</div>
            <div className="text-sm text-muted-foreground">{s.label}</div>
          </Link>
        ))}
      </div>

      {criticalIncidents.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <h2 className="font-display text-lg font-semibold">Critical & escalated</h2>
            <span className="text-xs text-muted-foreground">({criticalIncidents.length})</span>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {criticalIncidents.map((i) => (
              <Link key={i.id} to="/incidents/$incidentId" params={{ incidentId: i.id }}
                className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 transition-all hover:border-destructive">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[10px] text-destructive">{i.reference_number}</span>
                  {i.escalated && <span className="rounded-full bg-destructive/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-destructive">escalated</span>}
                </div>
                <div className="mt-2 line-clamp-2 font-medium">{i.title || i.subtype || i.category}</div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {new Date(i.occurrence_at).toLocaleString()} · <span className="capitalize">{i.status.replace("_", " ")}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-semibold">Live activity</h2>
            <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
              <span className="h-2 w-2 animate-pulse rounded-full bg-success" /> realtime
            </span>
          </div>
          <div className="mt-4">
            {currentOrg && <ActivityFeed orgId={currentOrg.id} limit={20} />}
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">Recent incidents</h2>
              <Link to="/incidents" className="text-xs text-primary hover:underline">View all</Link>
            </div>
            {recentIncidents.length === 0 ? (
              <div className="mt-4 rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">No incidents yet.</div>
            ) : (
              <ul className="mt-3 divide-y divide-border">
                {recentIncidents.map((i) => (
                  <li key={i.id}>
                    <Link to="/incidents/$incidentId" params={{ incidentId: i.id }} className="block py-2 hover:text-primary">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[10px] text-muted-foreground">{i.reference_number}</span>
                        <span className={`rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${i.severity === "critical" ? "bg-destructive/20 text-destructive" : i.severity === "high" ? "bg-warning/20 text-warning" : "bg-primary/15 text-primary"}`}>{i.severity}</span>
                      </div>
                      <div className="mt-0.5 truncate text-sm">{i.title || i.subtype || i.category}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <h2 className="font-display text-base font-semibold">Quick links</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link to="/sites" className="hover:text-primary">Manage sites</Link></li>
              <li><Link to="/team" className="hover:text-primary">Invite team</Link></li>
              <li><Link to="/schedule" className="hover:text-primary">Build schedule</Link></li>
              <li><Link to="/messages" className="hover:text-primary">Open messages</Link></li>
              <li><Link to="/geofences" className="hover:text-primary">Configure geofences</Link></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
